import { ChangeDetectorRef, computed, inject, Signal } from '@angular/core'
import { FormGroup, FormControl, Validators } from '@angular/forms'
import { ProxyValue, ValidationRules } from '@lg/shared'
import { ValidationUtil } from '../validation/validation-util.js'
import { Subscription } from 'rxjs'
import { FormDirective } from '../directives/form.directive.js'

export interface FormControlValueMocking<T> {
	getInitialValue: () => T
	onValueChange: (value: T) => void
}

export class FormControlManager {
	private static instances = new WeakMap<any, FormControlManager>()
	private static propertyCounter = 1
	// private controls = new Map<string | number, FormControl>()
	private controlSignals = new Map<
		string | number,
		Map<
			number,
			{
				controlSignal: () => FormControl
				hasValidators: boolean
			}
		>
	>() // propertyName => componentId => Signal<FormControl>

	constructor(private forObject: any) {}

	static getForObject(obj: any) {
		if (!FormControlManager.instances.has(obj)) {
			FormControlManager.instances.set(obj, new FormControlManager(obj))
		}
		return FormControlManager.instances.get(obj)!
	}

	static getFormControl(
		obj: any,
		propertyName: string | number,
		componentId: number,
		validationRules: ValidationRules | null,
		scope: any,
		formDirective: FormDirective,
		valueMocking?: FormControlValueMocking<any>,
	) {
		return FormControlManager.getForObject(obj).get(
			propertyName,
			componentId,
			validationRules,
			scope,
			formDirective,
			valueMocking,
		)
	}

	get(
		propertyName: string | number,
		componentId: number,
		validationRules: ValidationRules | null,
		scope: any,
		formDirective: FormDirective,
		valueMocking?: FormControlValueMocking<any>,
	) {
		let signalsForProperty = this.controlSignals.get(propertyName)!
		if (!signalsForProperty) {
			signalsForProperty = new Map()
			this.controlSignals.set(propertyName, signalsForProperty)
		}
		let signalForComponent = signalsForProperty.get(componentId)
		if (
			!signalForComponent ||
			signalForComponent.hasValidators != !!validationRules
		) {
			let fc: FormControl | undefined
			let subscription: Subscription | undefined

			const underlyingSignalForComponent = computed(() => {
				// must be computed signal to track the dependency on forObject[propertyName] and update the form control value
				let value = valueMocking
					? valueMocking.getInitialValue()
					: this.forObject[propertyName]
          value = value?.[ProxyValue] ?? value

				if (!fc || value !== fc.value) {
					// model value is different from form control value due to external change via script; re-create form control
					subscription?.unsubscribe()

					const validators = ValidationUtil.createSyncValidationFunction(
						validationRules?.sync,
						scope,
					)
					const asyncValidators = ValidationUtil.createAsyncValidationFunction(
						validationRules?.async,
						scope,
					)

					fc = new FormControl(value, {
						validators,
						asyncValidators,
					})
					subscription = fc.valueChanges.subscribe((formValue) => {
						// update the model value with the form control value; this will in turn trigger this computed signal to re-evaluate, but then the form control value
						// will be the same as the model value, thus not recreating the form control
						if (valueMocking) {
							valueMocking.onValueChange?.(formValue)
						} else {
							this.forObject[propertyName] = formValue
						}
					})
				}
				return fc
			})

			const controlPropertyName = `${this.forObject?.__type?.replaceAll('.', '_') ?? ''}_${propertyName}_${FormControlManager.propertyCounter++}`
			signalForComponent = {
				controlSignal: () => {
					// register form control on read to ensure that the form control is registered whenever it is displayed. The form control will be unregistered when the component is destroyed through the FormControlDestroyDirective
					const fc = underlyingSignalForComponent()
					formDirective.registerFormControl(controlPropertyName, fc)
					return fc
				},
				hasValidators: !!validationRules,
			}
			signalsForProperty.set(componentId, signalForComponent)
		}

		const formControl = signalForComponent!.controlSignal()
		return formControl
	}
}
;(globalThis as any).FormControlManager = FormControlManager
